import { useState } from 'react';
import {
  CircleCheckIcon,
  CircleXIcon,
  FileTextIcon,
  Loader2,
  MessageCircleMoreIcon,
  RefreshCcwIcon,
} from 'lucide-react';
import {
  Card,
  Button,
  Progress,
} from '@tinct/web-components';
import { cn } from '@tinct/core-utils';

export type SnippetCheckState = 'default' | 'in-progress' | 'success' | 'error';

export type SnippetCheckCardProps = {
  className?: string;
  state?: SnippetCheckState;
  progress?: number;
  errorMessage?: string | null;
  defaultUrl?: string;
  onVerify?: (url: string) => void;
  onRetry?: () => void;
};

export function SnippetCheckCard({
  className,
  state = 'default',
  progress = 0,
  errorMessage,
  defaultUrl,
  onVerify,
  onRetry,
}: SnippetCheckCardProps) {
  const [url, setUrl] = useState(defaultUrl ?? '');
  const [seededUrl, setSeededUrl] = useState(defaultUrl);

  // `defaultUrl` is fetched, so it usually lands after the first render: seed the
  // field when it changes, but never clobber what the user has already typed.
  // Adjusting state during render rather than in an effect is React's own answer
  // here — it re-renders before committing, instead of cascading a second pass.
  if (defaultUrl !== seededUrl) {
    setSeededUrl(defaultUrl);
    if (defaultUrl && !url) {
      setUrl(defaultUrl);
    }
  }

  const handleVerify = () => {
    onVerify?.(url);
  };

  return (
    <Card className={cn('gap-0 py-0', className)}>
      {/* Header */}
      <div className="flex flex-col gap-1.5 px-6 pt-6">
        <h3 className="font-heading text-xl font-semibold leading-7 tracking-[-0.3px] text-foreground">
          Verify installation
        </h3>
        {(state === 'default' || state === 'in-progress') && (
          <p className="text-sm text-muted-foreground">
            Enter your website URL to verify the snippet is correctly installed.
          </p>
        )}
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        {/* Default: URL input */}
        {state === 'default' && (
          <div className="flex items-center gap-6">
            <span className="text-base font-medium text-foreground">URL</span>
            <div className="flex-1">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://"
                className="h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
              />
            </div>
          </div>
        )}

        {/* In progress: spinner + progress bar */}
        {state === 'in-progress' && (
          <div className="flex flex-col items-center gap-0">
            <Loader2 className="size-8 animate-spin text-foreground" />
            <div className="flex w-full flex-col items-center gap-1">
              <span className="text-sm font-medium leading-4 text-foreground">
                Verifying installation....
              </span>
              <span className="text-sm text-muted-foreground">
                Checking connection to your site
              </span>
            </div>
            <Progress value={progress} className="mt-2 w-[400px]" />
          </div>
        )}

        {/* Success: green alert */}
        {state === 'success' && (
          <div className="flex items-start gap-4 rounded-2xl border border-teal-100 bg-[var(--colors-success-background)] p-4">
            <div className="pt-0.5">
              <CircleCheckIcon className="size-4 text-teal-600" />
            </div>
            <div className="flex flex-1 flex-col gap-2 text-sm leading-5 text-teal-600">
              <span className="font-medium">Snippet verified successfully!</span>
              <span>
                Your tracking is now active. Personalized pages will be shown to
                visitors from your target accounts.
              </span>
            </div>
          </div>
        )}

        {/* Error: red alert + checklist */}
        {state === 'error' && (
          <div className="flex flex-col gap-6">
            <div className="flex items-start gap-4 rounded-2xl border border-rose-100 bg-rose-50 p-4">
              <div className="pt-0.5">
                <CircleXIcon className="size-4 text-destructive" />
              </div>
              <div className="flex flex-1 flex-col gap-2 text-sm leading-5 text-destructive">
                <span className="font-medium">Verification failed</span>
                <span>
                  {errorMessage || "We couldn't detect the snippet on your site."}
                </span>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <p className="mb-0">Please check :</p>
              <ul className="mt-2 list-disc space-y-2 pl-5">
                <li>The code is placed before &lt;/head&gt;</li>
                <li>Your changes are deployed to production</li>
                <li>No script blockers are active</li>
                <li>Cache has been cleared (if using CDN)</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 border-t px-6 py-6">
        {state === 'default' && (
          <Button onClick={handleVerify}>
            Verify Installation
          </Button>
        )}

        {state === 'error' && (
          <>
            <Button onClick={onRetry}>
              <RefreshCcwIcon className="size-4" />
              Retry verification
            </Button>
            <Button variant="outline" asChild>
              <a
                href="https://help.tinct.ai/en/help/articles/4237570-verifying-your-installation-and-troubleshooting"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileTextIcon className="size-4" />
                Troubleshooting guide
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href="mailto:support@tinct.ai">
                <MessageCircleMoreIcon className="size-4" />
                Contact support
              </a>
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
